import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BellOff, CheckCheck, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { isToday, isYesterday, isThisWeek, parseISO } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import SubsectionHeader from "@/components/shared/SubsectionHeader";
import {
  useUserNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteUserNotification,
  NOTIFICATIONS_QUERY_KEY,
} from "@/hooks/useUserNotifications";
import { NotificationCard } from "@/components/notifications/NotificationCard";
import type { UserNotification } from "@/services/notificationService";
import { useQueryClient } from "@tanstack/react-query";

type FilterType = "all" | "unread" | "read";

type NotificationGroup = {
  today: UserNotification[];
  yesterday: UserNotification[];
  thisWeek: UserNotification[];
  older: UserNotification[];
};

export default function Notifications() {
  const { t } = useTranslation("common");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const queryClient = useQueryClient();

  // Infinite scroll hook
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useUserNotifications();

  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const deleteMutation = useDeleteUserNotification();

  // Flatten notifications
  const allNotifications = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.notifications);
  }, [data]);

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    let result = allNotifications;
    if (filterType === "unread") {
      result = allNotifications.filter((n) => !n.read_at);
    } else if (filterType === "read") {
      result = allNotifications.filter((n) => n.read_at);
    }
    return result;
  }, [allNotifications, filterType]);

  // Group notifications
  const groupedNotifications = useMemo<NotificationGroup>(() => {
    const groups: NotificationGroup = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    filteredNotifications.forEach((n) => {
      const date = parseISO(n.created_at);
      if (isToday(date)) {
        groups.today.push(n);
      } else if (isYesterday(date)) {
        groups.yesterday.push(n);
      } else if (isThisWeek(date)) {
        groups.thisWeek.push(n);
      } else {
        groups.older.push(n);
      }
    });
    return groups;
  }, [filteredNotifications]);

  // Stats
  const totalCount = allNotifications.length;
  const unreadCount = allNotifications.filter((n) => !n.read_at).length;

  const handleMarkAsRead = (id: number) => {
    markAsReadMutation.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [NOTIFICATIONS_QUERY_KEY],
          refetchType: "none",
        });
      },
    });
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const renderSection = (title: string, items: UserNotification[]) => {
    if (items.length === 0) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="mb-8 last:mb-0"
      >
        <div className="flex items-center gap-2 px-1 mb-3">
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          <span className="text-[10px] font-medium bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
            {items.length}
          </span>
        </div>
        <div className="space-y-3">
          {items.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending}
              isMarkingRead={markAsReadMutation.isPending}
            />
          ))}
        </div>
      </motion.div>
    );
  };

  const renderLoading = () => (
    <div className="space-y-8">
      {[1, 2].map((section) => (
        <div key={section} className="space-y-3">
          <Skeleton className="h-4 w-20 mb-4" />
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex gap-4 p-4 border rounded-2xl bg-card/50"
            >
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderEmpty = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center py-16 px-4"
    >
      <div className="bg-muted/30 p-6 rounded-full mb-4">
        <BellOff className="h-10 w-10 text-muted-foreground/40" />
      </div>
      <h3 className="text-lg font-medium text-foreground mb-2">
        {t("notifications.emptyState.title")}
      </h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm">
        {filterType === "unread"
          ? t("notifications.emptyState.noUnread")
          : filterType === "read"
            ? t("notifications.emptyState.noRead")
            : t("notifications.emptyState.noNotifications")}
      </p>
    </motion.div>
  );

  return (
    <div className="container mx-auto max-w-3xl px-4 py-4 min-h-[80vh]">
      <SubsectionHeader
        title={t("notifications.title")}
        description={t("notifications.description")}
        backPath="/"
        className="mb-6"
        primaryAction={
          unreadCount > 0
            ? {
                text: t("notifications.actions.markAllRead"),
                href: "#",
                icon: "CheckCheck",
                variant: "outline",
                size: "sm",
                dataAttrs: {
                  onClick: (e: any) => {
                    e.preventDefault();
                    handleMarkAllAsRead();
                  },
                },
              }
            : undefined
        }
      />

      <div className="mb-8">
        <Tabs
          value={filterType}
          onValueChange={(v) => setFilterType(v as FilterType)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3 p-1 bg-muted/50 rounded-xl">
            <TabsTrigger
              value="all"
              className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              {t("notifications.tabs.all")}
            </TabsTrigger>
            <TabsTrigger
              value="unread"
              className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              {t("notifications.tabs.unread")}
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/10 px-1 text-xs font-medium text-primary">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="read"
              className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              {t("notifications.tabs.read")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <AnimatePresence mode="wait">
        {isLoading && !data ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {renderLoading()}
          </motion.div>
        ) : filteredNotifications.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {renderEmpty()}
          </motion.div>
        ) : (
          <motion.div key="list" className="pb-10">
            {renderSection(
              t("notifications.sections.today"),
              groupedNotifications.today,
            )}
            {renderSection(
              t("notifications.sections.yesterday"),
              groupedNotifications.yesterday,
            )}
            {renderSection(
              t("notifications.sections.thisWeek"),
              groupedNotifications.thisWeek,
            )}
            {renderSection(
              t("notifications.sections.older"),
              groupedNotifications.older,
            )}

            {hasNextPage && filterType === "all" && (
              <div className="mt-8 text-center">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetching}
                  className="min-w-[140px] rounded-full"
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("notifications.actions.loading")}
                    </>
                  ) : (
                    t("notifications.actions.loadMore")
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
